import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { AuthContext } from '../context/AuthContext';
import { getDevicesWithUpdatesByCustomer, updateDeviceFirmwares, getCustomer } from '../utils/api';
import { useToast } from '../context/ToastContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faExclamationTriangle, faArrowLeft, faArrowUp, faArrowDown, faTimes } from '@fortawesome/free-solid-svg-icons';
import { formatRelativeDate } from '../utils/date';
import { ROLE_SUPERADMIN } from '../utils/roles';

const OrgDevicesPage = () => {
        const { customerId } = useParams();
        const { user } = useContext(AuthContext);
        const [devices, setDevices] = useState([]);
        const [customer, setCustomer] = useState(null);
        const [availableFirmware, setAvailableFirmware] = useState([]);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);
        const [firmwareVersions, setFirmwareVersions] = useState({});
        const [sortField, setSortField] = useState('id');
        const [sortDirection, setSortDirection] = useState('asc');
        const { showToast } = useToast();

        useEffect(() => {
                fetchData();
        }, [customerId]);

        const fetchData = async () => {
                try {
                        const [devicesResponse, customerResponse] = await Promise.all([
                                getDevicesWithUpdatesByCustomer(customerId),
                                getCustomer(customerId)
                        ]);

                        if (!devicesResponse.ok || !customerResponse.ok) {
                                throw new Error('Failed to fetch data');
                        }

                        const devicesData = await devicesResponse.json();
                        const customerData = await customerResponse.json();

                        setDevices(devicesData.data.devices || []);
                        setAvailableFirmware(devicesData.data.fw || []);
                        setCustomer(customerData.data);
                } catch (err) {
                        setError(err.message);
                } finally {
                        setLoading(false);
                }
        };

        const handleFirmwareChange = (deviceId, value) => {
                setFirmwareVersions(prev => ({
                        ...prev,
                        [deviceId]: value
                }));
        };

        const handleCancelUpgrade = (deviceId) => {
                setFirmwareVersions(prev => ({
                        ...prev,
                        [deviceId]: ''
                }));
        };

        const handleSubmit = async () => {
                const updates = Object.keys(firmwareVersions)
                        .filter(deviceId => firmwareVersions[deviceId] || firmwareVersions[deviceId] === '')
                        .map(deviceId => {
                                const device = devices.find(d => d.id === parseInt(deviceId));
                                return {
                                        id: device?.device_update?.id || 0,
                                        device_id: parseInt(deviceId),
                                        requested_firmware: firmwareVersions[deviceId]
                                };
                        });

                if (updates.length === 0) {
                        showToast('error', 'Please select firmware versions to update');
                        return;
                }

                try {
                        const response = await updateDeviceFirmwares(updates);
                        if (!response.ok) {
                                throw new Error('Failed to update firmware');
                        }
                        showToast('success', 'Firmware updates saved successfully');
                        fetchData();
                        setFirmwareVersions({});
                } catch (err) {
                        showToast('error', err.message);
                }
        };

        const getStatus = (device) => {
                if (!device.device_update || !device.device_update.requested_firmware) {
                        return 'OK';
                }
                if (device.firmware_version !== device.device_update.requested_firmware) {
                        return 'Upgrade Requested';
                }
                return 'OK';
        };

        const handleSort = (field) => {
                if (sortField === field) {
                        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                } else {
                        setSortField(field);
                        setSortDirection('asc');
                }
        };

        const getSortIcon = (field) => {
                if (sortField !== field) return null;
                return sortDirection === 'asc' ? faArrowUp : faArrowDown;
        };

        const sortedDevices = useMemo(() => {
                const sorted = [...devices].sort((a, b) => {
                        let aValue, bValue;

                        switch (sortField) {
                                case 'id':
                                        aValue = a.id || 0;
                                        bValue = b.id || 0;
                                        break;
                                case 'hardware_version':
                                        aValue = a.hardware_version || '';
                                        bValue = b.hardware_version || '';
                                        break;
                                case 'firmware_version':
                                        aValue = a.firmware_version || '';
                                        bValue = b.firmware_version || '';
                                        break;
                                case 'updated_at':
                                        aValue = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                                        bValue = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                                        break;
                                case 'status':
                                        aValue = getStatus(a);
                                        bValue = getStatus(b);
                                        break;
                                default:
                                        return 0;
                        }

                        if (typeof aValue === 'string') {
                                const comparison = aValue.localeCompare(bValue);
                                return sortDirection === 'asc' ? comparison : -comparison;
                        }

                        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
                });

                return sorted;
        }, [devices, sortField, sortDirection]);

        if (user?.roleId !== ROLE_SUPERADMIN) {
                return <Navigate to="/" replace />;
        }

        if (loading) {
                return <div className="page-gradient min-h-screen flex items-center justify-center">Loading...</div>;
        }

        if (error) {
                return <div className="page-gradient min-h-screen flex items-center justify-center text-status-critical">{error}</div>;
        }

        return (
                <div className="page-gradient min-h-screen">
                        <Header />

                        <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
                                <section className="mb-8">
                                        <Link to="/org" className="inline-flex items-center text-sm text-text-weak hover:text-text-strong mb-4">
                                                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                                                Back to Organizations
                                        </Link>
                                        <div className="flex items-center justify-between">
                                                <div>
                                                        <h1 data-testid="org-devices-title" className="text-3xl font-bold text-text-strong mb-2">Devices - {customer?.name}</h1>
                                                        <p className="text-text-weak">Manage firmware updates for this organization</p>
                                                </div>
                                                <button
                                                        onClick={handleSubmit}
                                                        data-testid="devices-save-updates"
                                                        disabled={Object.keys(firmwareVersions).length === 0}
                                                        className={`inline-flex items-center px-4 py-2 rounded-xl transition-colors ${Object.keys(firmwareVersions).length === 0
                                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                                : 'bg-brand text-white hover:bg-brand/90'
                                                                }`}
                                                >
                                                        Save Updates
                                                </button>
                                        </div>
                                </section>

                                <div className="bg-white/60 backdrop-blur-sm rounded-2xl shadow-card overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                        <tr>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('id')}
                                                                        data-testid="sort-id"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Device ID</span>
                                                                                {sortField === 'id' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('id')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('hardware_version')}
                                                                        data-testid="sort-hardware-version"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>HW Rev.</span>
                                                                                {sortField === 'hardware_version' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('hardware_version')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('firmware_version')}
                                                                        data-testid="sort-firmware-version"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Current FW</span>
                                                                                {sortField === 'firmware_version' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('firmware_version')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('updated_at')}
                                                                        data-testid="sort-updated-at"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Last Updated</span>
                                                                                {sortField === 'updated_at' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('updated_at')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Firmware</th>
                                                                <th
                                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                                                        onClick={() => handleSort('status')}
                                                                        data-testid="sort-status"
                                                                >
                                                                        <div className="flex items-center space-x-1">
                                                                                <span>Status</span>
                                                                                {sortField === 'status' ? (
                                                                                        <FontAwesomeIcon icon={getSortIcon('status')} className="text-gray-400" />
                                                                                ) : (
                                                                                        <div className="flex flex-col text-gray-400">
                                                                                                <FontAwesomeIcon icon={faArrowUp} className="text-[8px] -mb-0.5" />
                                                                                                <FontAwesomeIcon icon={faArrowDown} className="text-[8px]" />
                                                                                        </div>
                                                                                )}
                                                                        </div>
                                                                </th>
                                                        </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                        {sortedDevices.map(device => {
                                                                const status = getStatus(device);
                                                                return (
                                                                        <tr key={device.id} data-testid="device-row" data-device-id={device.id}>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                                        {device.id}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                                        {device.hardware_version || '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                                        {device.firmware_version || '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                                                        {device.updated_at ? formatRelativeDate(device.updated_at) : '-'}
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                                        <select
                                                                                                value={firmwareVersions[device.id] || device.device_update?.requested_firmware || ''}
                                                                                                data-testid="device-firmware-select"
                                                                                                onChange={(e) => handleFirmwareChange(device.id, e.target.value)}
                                                                                                className="px-2 py-1 border rounded-md text-sm w-32"
                                                                                        >
                                                                                                <option value="">Select version</option>
                                                                                                {availableFirmware.filter(fw => fw.hardware === device.hardware_version).map(fw => (
                                                                                                        <option key={fw.id} value={fw.version}>
                                                                                                                {fw.version}
                                                                                                        </option>
                                                                                                ))}
                                                                                        </select>
                                                                                </td>
                                                                                <td className="px-6 py-4 whitespace-nowrap">
                                                                                        <div className="flex items-center gap-2">
                                                                                                {firmwareVersions[device.id] === '' ? (
                                                                                                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                                                                                                <FontAwesomeIcon icon={faTimes} className="mr-1" />
                                                                                                                Cancel Requested
                                                                                                        </span>
                                                                                                ) : (
                                                                                                        <>
                                                                                                                <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                                                                                                        }`}>
                                                                                                                        <FontAwesomeIcon
                                                                                                                                icon={status === 'OK' ? faCheckCircle : faExclamationTriangle}
                                                                                                                                className="mr-1"
                                                                                                                        />
                                                                                                                        {status}
                                                                                                                </span>
                                                                                                                {status === 'Upgrade Requested' && (
                                                                                                                        <button
                                                                                                                                onClick={() => handleCancelUpgrade(device.id)}
                                                                                                                                data-testid="device-cancel-upgrade"
                                                                                                                                className="text-gray-400 hover:text-red-500 transition-colors"
                                                                                                                                title="Cancel upgrade"
                                                                                                                        >
                                                                                                                                <FontAwesomeIcon icon={faTimes} />
                                                                                                                        </button>
                                                                                                                )}
                                                                                                        </>
                                                                                                )}
                                                                                        </div>
                                                                                </td>
                                                                        </tr>
                                                                );
                                                        })}
                                                </tbody>
                                        </table>
                                </div>

                                {devices.length === 0 && (
                                        <div className="text-center py-12">
                                                <p className="text-lg text-gray-600">No devices found.</p>
                                        </div>
                                )}
                        </main>

                        <Footer />
                </div>
        );
};

export default OrgDevicesPage;
